import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, Button, Row, Col, Typography, Space } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiColegas, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import PacienteFotoPicker from "./PacienteFotoPicker";
import { fotoUrl } from "./EntidadFotoCard";

import "./PacientesPage.css";

const { Title, Text } = Typography;

export default function CrearColegaPage() {
  const navigate = useNavigate();
  const { idColega } = useParams();
  const isEditing = !!idColega;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEditing);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoActual, setFotoActual] = useState(null);
  const [success, setSuccess] = useState({ show: false, title: "", subtitle: "", to: "/app/colegas" });

  useEffect(() => {
    if (!isEditing) return;
    apiColegas
      .get(`/colegas/${idColega}`, { headers: authHeader() })
      .then((res) => {
        const item = res.data ?? {};
        form.setFieldsValue({
          nombre: item.nombre,
          especialidad: item.especialidad,
          telefono: item.telefono,
          correo: item.correo,
        });
        setFotoActual(fotoUrl(item));
      })
      .catch(() => toast.error("No se pudo cargar el colega"))
      .finally(() => setLoadingItem(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idColega]);

  const subirFoto = async (id) => {
    if (!fotoFile) return;
    const fd = new FormData();
    fd.append("file", fotoFile);
    try {
      await apiColegas.post(`/colegas/${id}/foto`, fd, { headers: authHeader() });
    } catch (err) {
      toast.warning(err?.response?.data?.detail || "El colega se guardó, pero no se pudo subir la imagen");
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      nombre: values.nombre?.trim(),
      especialidad: values.especialidad?.trim() || null,
      telefono: values.telefono?.trim() || null,
      correo: values.correo?.trim() || null,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await apiColegas.put(`/colegas/${idColega}`, payload, { headers: authHeader() });
        await subirFoto(idColega);
        setSuccess({ show: true, title: "Colega actualizado", subtitle: "Los cambios se guardaron correctamente.", to: `/app/colegas/${idColega}` });
      } else {
        const { data } = await apiColegas.post("/colegas", payload, { headers: authHeader() });
        await subirFoto(data?.id);
        setSuccess({ show: true, title: "Colega creado", subtitle: "Se agregó al directorio correctamente.", to: `/app/colegas/${data?.id}` });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "No se pudo guardar el colega");
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="paq-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando colega" : "Nuevo colega"}
            </Title>
            <Text className="paq-subtitle">
              {isEditing
                ? "Modifica los datos del colega y guarda los cambios."
                : "Completa los datos para agregarlo al directorio de colegas."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate(-1)} disabled={saving || success.show}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={success.show || loadingItem}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              {isEditing ? "Guardar cambios" : "Crear colega"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" disabled={loadingItem}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-user-doctor" /></span>
              Datos del colega
            </div>

            <Row gutter={16}>
              <Col xs={24}>
                <div className="paq-field-label">Imagen del colega (opcional)</div>
                <PacienteFotoPicker value={fotoActual} onChange={setFotoFile} icon="fa-user-doctor" />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre"
                  label={<span className="paq-field-label">Nombre</span>}
                  rules={[{ required: true, whitespace: true, message: "Requerido" }]}
                >
                  <Input placeholder="Nombre completo del médico" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="especialidad" label={<span className="paq-field-label">Especialidad (opcional)</span>}>
                  <Input placeholder="Ej. Medicina general" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="telefono" label={<span className="paq-field-label">Teléfono (opcional)</span>}>
                  <Input placeholder="Número de contacto" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="correo"
                  label={<span className="paq-field-label">Correo (opcional)</span>}
                  rules={[{ type: "email", message: "Ingresa un correo válido" }]}
                >
                  <Input placeholder="correo@ejemplo.com" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <SuccessOverlay
        show={success.show}
        title={success.title}
        subtitle={success.subtitle}
        onDone={() => navigate(success.to)}
      />
    </main>
  );
}
